import { css, SerializedStyles } from '@emotion/react';
import React, { useState } from 'react';

import { useBulkPhotoUploader } from '@/lib/photoLib';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { ScanPartContext, ScanPhoto } from '@kaiizen/kai-scan-template-system';
import { UploadIcon } from '@kaiizen/kai-shiki/react';
import { TYPOGRAPHY2 } from '../Styles';

const PHOTO_DROP_ZONE_STYLE = css`
  label: PHOTO_DROP_ZONE_STYLE;
  position: relative;
`;

/** A light blue-grey scrim, not just an outline — the whole zone reads as "drop
 *  here" at a glance rather than requiring a reader to spot a thin border. */
export const DRAG_OVERLAY_STYLE = css`
  label: PHOTO_DROP_ZONE_DRAG_OVERLAY_STYLE;
  position: absolute;
  inset: 0;
  background: var(--backgroundBlue2);
  opacity: 0.82;
  border: 2px solid var(--textBlue);
  pointer-events: none;
  z-index: 1;
`;

const DRAG_OVERLAY_LABEL_STYLE = css`
  label: PHOTO_DROP_ZONE_DRAG_OVERLAY_LABEL_STYLE;
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  ${TYPOGRAPHY2.smallBold}
  color: var(--textBlue);
  pointer-events: none;
  z-index: 2;
`;

type PhotoDropZoneTarget = Parameters<ReturnType<typeof useBulkPhotoUploader>>[1] | null | undefined;

export const usePhotoDropZone = (
  scanPart: PhotoDropZoneTarget,
  scanPartContext: ScanPartContext,
  onPhotosAdded?: (photos: ScanPhoto[]) => void,
) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const bulkPhotoUploader = useBulkPhotoUploader();
  const { scanIsEditable } = useScanEditingContextSlice(['scanIsEditable']);

  const handlers = {
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!scanIsEditable || !scanPart) return;
      const hasImageFiles = Array.from(event.dataTransfer.items || []).some(item => item.type.startsWith('image/'));
      if (hasImageFiles) setIsDragOver(true);
    },

    onDragLeave: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      const { clientX, clientY } = event;
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        setIsDragOver(false);
      }
    },

    onDrop: async (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      if (!scanIsEditable || !scanPart) return;
      const photos = await bulkPhotoUploader(event.dataTransfer.files, scanPart, scanPartContext);
      if (photos?.length) onPhotosAdded?.(photos);
    },
  };

  return { isDragOver, handlers };
};

export const PhotoDropZone = ({
  scanPart,
  scanPartContext,
  children,
  dropZoneCss,
  onPhotosAdded,
  label = 'Drop photos to upload',
}: {
  scanPart: PhotoDropZoneTarget;
  scanPartContext: ScanPartContext;
  children: React.ReactNode;
  dropZoneCss?: SerializedStyles;
  onPhotosAdded?: (photos: ScanPhoto[]) => void;
  /** What the overlay says while a file is dragged over — callers with a more
   *  specific scope in view (a task or group's own name) should override this. */
  label?: string;
}) => {
  const { isDragOver, handlers } = usePhotoDropZone(scanPart, scanPartContext, onPhotosAdded);

  return (
    <div
      css={[PHOTO_DROP_ZONE_STYLE, dropZoneCss]}
      {...handlers}
    >
      {isDragOver
        ? (
          <>
            <div css={DRAG_OVERLAY_STYLE} />
            <div css={DRAG_OVERLAY_LABEL_STYLE}>
              <UploadIcon size={18} aria-hidden='true' />
              {label}
            </div>
          </>
        )
        : null}
      {children}
    </div>
  );
};
